// Peak data loader for the Konva timeline waveform.
//
// Three sources, in order of preference:
//   1. `media.peaksPath`   - fetch JSON, accept either `{ data }` or raw array
//   2. `media.peaksData`   - inline array on the media object
//   3. Web Audio extraction for shortform browser/desktop media (fetchable URL,
//      duration under a threshold)
//   4. Flat fallback (a single channel of zeros)
//
// All return paths produce the normalized shape `{ data: number[][] }`.

export const FLAT_FALLBACK_PEAKS = Object.freeze({ data: [[0]], sampleRate: 8000 });

const FETCHABLE_SCHEMES = /^(https?:|file:|blob:|data:)/i;
const URL_STORAGE_SKIP = new Set(["YouTube", "Vimeo", "HLS Manifest"]);
const DEFAULT_MAX_DURATION_SEC = 300;       // 5 minutes
const DEFAULT_SAMPLES_PER_SECOND = 100;     // 100 peaks per second = ~smooth
const MIN_MAX_PAIR_SAMPLE_LIMIT = 2000;

/**
 * Resolve peak data for a media object.
 *
 * @param {object} media - `projectState.media`
 * @param {object} [options]
 * @param {number} [options.durationSec]            - media duration (skips Web Audio if too long)
 * @param {number} [options.maxDurationSec]
 * @param {number} [options.samplesPerSecond]
 * @returns {Promise<{data: number[][]}>}
 */
export async function loadPeaks(media, options = {}) {
    if (!media) return FLAT_FALLBACK_PEAKS;
    const {
        durationSec = 0,
        maxDurationSec = DEFAULT_MAX_DURATION_SEC,
        samplesPerSecond = DEFAULT_SAMPLES_PER_SECOND
    } = options;

    // 1. Pre-computed peaks at a path (desktop ffmpeg extraction).
    if (media.peaksPath) {
        try {
            const res = await fetch(media.peaksPath);
            const json = await res.json();
            const normalized = normalizePeaks(json);
            if (normalized) return normalized;
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("[timeline.peaks] Failed to load peaksPath:", err?.message);
        }
    }

    // 2. Inline peaks (when host app already has them in memory).
    if (Array.isArray(media.peaksData) && media.peaksData.length > 0) {
        const normalized = normalizePeaks(media.peaksData);
        if (normalized) return normalized;
    }

    // 3. Web Audio extraction (browser shortform). Cheap heuristic: only try
    // when we have a fetchable URL, the duration is bounded, and the source is
    // not a streaming/proprietary provider that won't return raw audio.
    if (canExtractWithWebAudio(media, durationSec, maxDurationSec)) {
        try {
            const extracted = await extractPeaksWithWebAudio(
                pickFetchableUrl(media),
                samplesPerSecond
            );
            if (extracted && Array.isArray(extracted.data) && extracted.data.length > 0) {
                return extracted;
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("[timeline.peaks] Web Audio extraction failed:", err?.message);
        }
    }

    // 4. Flat fallback. Renders as a flat baseline.
    return FLAT_FALLBACK_PEAKS;
}

/**
 * Convert supported peak payloads into the renderer contract:
 * `{ data: number[][] }`, where each value is a `0..1` amplitude.
 *
 * This accepts both existing normalized CCC peak arrays and raw min/max pair
 * payloads from tools such as `audiowaveform`.
 *
 * @param {object|number[]|number[][]} payload
 * @returns {{data: number[][]}|null}
 */
export function normalizePeaks(payload) {
    const source = Array.isArray(payload) ? { data: payload } : payload;
    if (!source || !Array.isArray(source.data)) return null;

    const rawChannels = isNumericArray(source.data)
        ? [source.data]
        : source.data.filter(Array.isArray);
    const data = rawChannels
        .map((channel) => normalizePeakChannel(channel, source))
        .filter((channel) => channel.length > 0);

    if (data.length === 0) return null;
    return { ...source, data };
}

function normalizePeakChannel(channel, metadata = {}) {
    const values = channel.filter(isFiniteNumber);
    if (values.length === 0) return [];

    const amplitudes = isMinMaxPairChannel(values, metadata)
        ? collapseMinMaxPairs(values)
        : values.map((value) => Math.abs(value));
    const maxAbs = amplitudes.reduce((max, value) => Math.max(max, value), 0);
    const divisor = peakValueDivisor(metadata, maxAbs);

    return amplitudes.map((value) => clamp01(value / divisor));
}

function collapseMinMaxPairs(values) {
    const amplitudes = [];
    for (let i = 0; i < values.length - 1; i += 2) {
        amplitudes.push(Math.max(Math.abs(values[i]), Math.abs(values[i + 1])));
    }
    return amplitudes;
}

function isMinMaxPairChannel(values, metadata = {}) {
    if (values.length < 2 || values.length % 2 !== 0) return false;

    const declaredLength = Number(metadata.length);
    if (Number.isFinite(declaredLength) && declaredLength > 0 && values.length === declaredLength * 2) {
        return true;
    }

    if (!values.some((value) => value < 0) || !values.some((value) => value > 0)) {
        return false;
    }

    const pairCount = values.length / 2;
    const stride = Math.max(1, Math.floor(pairCount / MIN_MAX_PAIR_SAMPLE_LIMIT));
    let checked = 0;
    let matchingPairs = 0;

    for (let pair = 0; pair < pairCount; pair += stride) {
        const first = values[pair * 2];
        const second = values[pair * 2 + 1];
        if (first === 0 && second === 0) continue;
        checked += 1;
        if ((first <= 0 && second >= 0) || (first >= 0 && second <= 0)) {
            matchingPairs += 1;
        }
    }

    return checked > 0 && matchingPairs / checked >= 0.7;
}

function peakValueDivisor(metadata = {}, maxAbs = 0) {
    if (maxAbs <= 1) return 1;
    const bits = Number(metadata.bits);
    if (Number.isFinite(bits) && bits > 1) {
        return 2 ** (bits - 1);
    }
    return maxAbs;
}

function isNumericArray(value) {
    return Array.isArray(value) && value.some(isFiniteNumber);
}

function isFiniteNumber(value) {
    return typeof value === "number" && Number.isFinite(value);
}

function clamp01(value) {
    if (!Number.isFinite(value) || value <= 0) return 0;
    if (value >= 1) return 1;
    return value;
}

function pickFetchableUrl(media) {
    if (!media) return "";
    if (media.localPath) {
        // Electron: localPath is OS-native. Convert to file:// URL.
        if (FETCHABLE_SCHEMES.test(media.localPath)) return media.localPath;
        const normalized = media.localPath.replace(/\\/g, "/");
        return /^[a-z]:\//i.test(normalized) ? `file:///${normalized}` : `file://${normalized}`;
    }
    if (typeof media.path === "string" && FETCHABLE_SCHEMES.test(media.path)) {
        return media.path;
    }
    if (typeof media.url === "string" && FETCHABLE_SCHEMES.test(media.url)) {
        return media.url;
    }
    return "";
}

function canExtractWithWebAudio(media, durationSec, maxDurationSec) {
    if (typeof window === "undefined") return false;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return false;
    if (!media) return false;
    if (URL_STORAGE_SKIP.has(media.storage)) return false;
    if (durationSec && durationSec > maxDurationSec) return false;
    return Boolean(pickFetchableUrl(media));
}

/**
 * Public wrapper for Web Audio peak extraction. Use this for short audio files
 * outside the project-media pipeline (e.g. per-event audio for audio
 * description events).
 *
 * @param {string} url
 * @param {object} [options]
 * @param {number} [options.samplesPerSecond]
 * @returns {Promise<{data: number[][]}>}
 */
export async function extractPeaksFromUrl(url, { samplesPerSecond = DEFAULT_SAMPLES_PER_SECOND } = {}) {
    return extractPeaksWithWebAudio(url, samplesPerSecond);
}

/**
 * Decode an audio file via Web Audio and bucket its samples into per-second
 * peaks. Mixes all channels to mono.
 *
 * @param {string} url
 * @param {number} samplesPerSecond
 * @returns {Promise<{data: number[][]}>}
 */
async function extractPeaksWithWebAudio(url, samplesPerSecond) {
    if (!url) throw new Error("No fetchable URL");
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buf = await res.arrayBuffer();
        const audioBuffer = await ctx.decodeAudioData(buf);
        const totalDuration = audioBuffer.duration || 0;
        const totalBuckets = Math.max(1, Math.ceil(totalDuration * samplesPerSecond));
        const samples = new Float32Array(totalBuckets);

        // Mix all channels to mono and pick the max-absolute-amplitude per bucket.
        for (let ch = 0; ch < audioBuffer.numberOfChannels; ch += 1) {
            const channelData = audioBuffer.getChannelData(ch);
            const samplesPerBucket = channelData.length / totalBuckets;
            for (let b = 0; b < totalBuckets; b += 1) {
                const startIdx = Math.floor(b * samplesPerBucket);
                const endIdx = Math.min(channelData.length, Math.floor((b + 1) * samplesPerBucket));
                let peak = 0;
                for (let i = startIdx; i < endIdx; i += 1) {
                    const v = channelData[i];
                    const abs = v < 0 ? -v : v;
                    if (abs > peak) peak = abs;
                }
                if (peak > samples[b]) samples[b] = peak;
            }
        }

        return { data: [Array.from(samples)] };
    } finally {
        if (typeof ctx.close === "function") {
            try { await ctx.close(); } catch { /* noop */ }
        }
    }
}
